const attributeNames = {
  date: 'Date de livraison souhaitée',
  window: 'Créneau souhaité',
  instructions: 'Instructions de livraison',
  gps: 'Repère GPS de livraison',
};
const initialized = new WeakMap();
let pendingSave = Promise.resolve();
let latestQueued = null;
let latestLocalValues = null;
let saveTimer;
let gpsRequestId = 0;

function currentRoot() {
  return document.querySelector('[data-bf-cart-delivery]');
}

function setStatus(root, state) {
  const status = root?.querySelector('[data-bf-delivery-status]');
  if (!status) return;
  status.textContent = root.dataset[state] || '';
  status.toggleAttribute('data-error', state === 'error');
}

function minimumDate(root) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const part = (name) => parts.find((item) => item.type === name).value;
  const date = new Date(`${part('year')}-${part('month')}-${part('day')}T12:00:00Z`);
  const leadDays = Number(root?.dataset.leadDays ?? 1);
  date.setUTCDate(date.getUTCDate() + Math.min(7, Math.max(0, Number.isFinite(leadDays) ? leadDays : 1)));
  if (root?.dataset.closedSunday !== 'false' && date.getUTCDay() === 0) date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}

function checkDate(root) {
  const input = root.querySelector('[data-bf-delivery-field="date"]');
  if (!input || input.type !== 'date') return true;
  const isSunday = root.dataset.closedSunday !== 'false' && input.value && new Date(`${input.value}T12:00:00Z`).getUTCDay() === 0;
  const valid = !input.value || (input.value >= input.min && !isSunday);
  input.setCustomValidity(valid ? '' : root.dataset.invalidDate);
  return valid;
}

function normalizeGps(value) {
  const text = value.trim();
  if (!text) return { valid: true, value: '' };
  if (text.length > 500) return { valid: false, value: '' };
  const coordinates = text.match(/^([+-]?\d{1,3}(?:\.\d+)?)\s*[,;]\s*([+-]?\d{1,3}(?:\.\d+)?)$/);
  if (coordinates) {
    const latitude = Number(coordinates[1]);
    const longitude = Number(coordinates[2]);
    if (Number.isFinite(latitude) && Number.isFinite(longitude) && Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180) {
      return { valid: true, value: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}` };
    }
    return { valid: false, value: '' };
  }
  try {
    const url = new URL(text);
    if (url.protocol !== 'https:' || url.username || url.password || url.port) return { valid: false, value: '' };
    const host = url.hostname.toLowerCase();
    const googleMaps = ['google.com', 'www.google.com', 'google.fr', 'www.google.fr'].includes(host) && /^\/maps(?:\/|$)/.test(url.pathname);
    const googleShortLink = host === 'maps.app.goo.gl' && /^\/[a-zA-Z0-9_-]+\/?$/.test(url.pathname);
    const legacyGoogleLink = host === 'goo.gl' && /^\/maps\/[a-zA-Z0-9_-]+\/?$/.test(url.pathname);
    const mapHost = ['maps.google.com', 'maps.google.fr', 'maps.apple.com'].includes(host);
    if (googleMaps || googleShortLink || legacyGoogleLink || mapHost) return { valid: true, value: url.href };
  } catch {}
  return { valid: false, value: '' };
}

function setGpsStatus(root, state) {
  const status = root.querySelector('[data-bf-gps-status]');
  if (!status) return;
  status.textContent = state ? root.dataset[state] || '' : '';
  status.toggleAttribute('data-error', ['gpsInvalid', 'gpsDenied', 'gpsUnavailable'].includes(state));
}

function setGpsValue(root, value, state = '') {
  const input = root.querySelector('[data-bf-gps-input]');
  const attribute = root.querySelector('[data-bf-delivery-field="gps"]');
  if (!input || !attribute) return;
  const result = normalizeGps(value);
  input.value = value;
  attribute.value = result.value;
  input.toggleAttribute('aria-invalid', !result.valid);
  if (!result.valid) input.setAttribute('aria-invalid', 'true');
  setGpsStatus(root, result.valid ? state : 'gpsInvalid');
}

function cancelGpsRequest(root) {
  gpsRequestId += 1;
  const button = root.querySelector('[data-bf-gps-locate]');
  if (!button) return;
  button.disabled = false;
  button.removeAttribute('aria-busy');
}

function locateGps(root) {
  if (root.dataset.local !== 'true') return;
  if (!navigator.geolocation) {
    setGpsStatus(root, 'gpsUnavailable');
    return;
  }
  const requestId = ++gpsRequestId;
  const button = root.querySelector('[data-bf-gps-locate]');
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  setGpsStatus(root, 'gpsLocating');
  const finish = () => {
    if (requestId !== gpsRequestId || currentRoot() !== root || !root.isConnected || root.dataset.local !== 'true') return false;
    button.disabled = false;
    button.removeAttribute('aria-busy');
    return true;
  };
  navigator.geolocation.getCurrentPosition((position) => {
    if (!finish()) return;
    const result = normalizeGps(`${position.coords.latitude}, ${position.coords.longitude}`);
    if (!result.valid) {
      setGpsStatus(root, 'gpsUnavailable');
      return;
    }
    setGpsValue(root, result.value, 'gpsLocated');
    clearTimeout(saveTimer);
    queueSave(root);
  }, (error) => {
    if (!finish()) return;
    setGpsStatus(root, error.code === 1 ? 'gpsDenied' : 'gpsUnavailable');
  }, { enableHighAccuracy: false, timeout: 10000, maximumAge: 0 });
}

function attributesFor(root) {
  const attributes = {};
  for (const field of root.querySelectorAll('[data-bf-delivery-field]')) {
    attributes[attributeNames[field.dataset.bfDeliveryField]] = field.value;
  }
  return attributes;
}

function queueSave(root, attributes = root ? attributesFor(root) : null) {
  if (!attributes || (root && !checkDate(root))) return pendingSave;
  const serialized = JSON.stringify(attributes);
  if (root?.dataset.local === 'true') latestLocalValues = attributes;
  if (serialized === latestQueued) return pendingSave;
  latestQueued = serialized;
  if (!root?.querySelector('[data-bf-delivery-status]')?.hasAttribute('data-error')) setStatus(root, 'saving');
  pendingSave = pendingSave.catch(() => {}).then(async () => {
    const routeRoot = window.Shopify?.routes?.root || '/';
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    let response;
    try {
      response = await fetch(`${routeRoot}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timeout);
    }
    if (!response.ok) throw new Error('Cart preference update failed');
    const cart = await response.json();
    for (const [key, value] of Object.entries(attributes)) {
      if ((cart.attributes?.[key] || '') !== value) throw new Error('Cart preference update mismatch');
    }
    if (currentRoot() && JSON.stringify(attributesFor(currentRoot())) === serialized) setStatus(currentRoot(), 'saved');
  }).catch((error) => {
    if (latestQueued === serialized) latestQueued = null;
    setStatus(currentRoot(), 'error');
    throw error;
  });
  pendingSave.catch(() => {});
  return pendingSave;
}

function initializeRoots() {
  const roots = document.querySelectorAll('[data-bf-cart-delivery]');
  const summaries = [...document.querySelectorAll('[data-bf-cart-kind]')];
  const localSummary = summaries.some((summary) => summary.dataset.bfCartKind === 'local');
  const obsoleteSummary = summaries.some((summary) => summary.dataset.bfCartKind !== 'local' && summary.dataset.bfHasDeliveryPreferences === 'true');
  if (!localSummary && obsoleteSummary && ![...roots].some((root) => root.dataset.local === 'true')) {
    clearTimeout(saveTimer);
    gpsRequestId += 1;
    latestLocalValues = null;
    queueSave(null, Object.fromEntries(Object.values(attributeNames).map((name) => [name, ''])));
  }
  const cartPage = document.querySelector('cart-items-component .cart-page');
  if (!roots.length && cartPage && !cartPage.querySelector('#cart-form')) {
    clearTimeout(saveTimer);
    gpsRequestId += 1;
    latestLocalValues = null;
    queueSave(null, Object.fromEntries(Object.values(attributeNames).map((name) => [name, ''])));
    return;
  }
  for (const root of roots) {
    if (initialized.get(root) === root.dataset.local) continue;
    initialized.set(root, root.dataset.local);
    if (root.dataset.local !== 'true') {
      cancelGpsRequest(root);
      latestLocalValues = null;
      for (const field of root.querySelectorAll('[data-bf-delivery-field]')) field.value = '';
      queueSave(root);
      continue;
    }
    if (latestLocalValues) {
      for (const field of root.querySelectorAll('[data-bf-delivery-field]')) {
        field.value = latestLocalValues[attributeNames[field.dataset.bfDeliveryField]] || '';
      }
    }
    if (latestQueued === null) latestQueued = JSON.stringify(attributesFor(root));
    const gpsAttribute = root.querySelector('[data-bf-delivery-field="gps"]');
    const previousGps = gpsAttribute?.value || '';
    setGpsValue(root, previousGps);
    const input = root.querySelector('[data-bf-delivery-field="date"]');
    input.min = minimumDate(root);
    if (!checkDate(root)) {
      input.value = '';
      checkDate(root);
      queueSave(root);
    }
    if (gpsAttribute && gpsAttribute.value !== previousGps) queueSave(root);
  }
}

document.addEventListener('input', (event) => {
  const root = event.target.closest?.('[data-bf-cart-delivery]');
  if (!root || !event.target.matches('[data-bf-delivery-field], [data-bf-gps-input]')) return;
  if (event.target.matches('[data-bf-gps-input]')) {
    cancelGpsRequest(root);
    setGpsValue(root, event.target.value);
  }
  clearTimeout(saveTimer);
  checkDate(root);
  latestLocalValues = attributesFor(root);
  setStatus(root, 'saving');
  saveTimer = setTimeout(() => queueSave(currentRoot()), 250);
});

document.addEventListener('change', (event) => {
  const root = event.target.closest?.('[data-bf-cart-delivery]');
  if (!root || !event.target.matches('[data-bf-delivery-field], [data-bf-gps-input]')) return;
  clearTimeout(saveTimer);
  queueSave(root);
});

document.addEventListener('click', (event) => {
  const button = event.target.closest?.('[data-bf-gps-locate], [data-bf-gps-clear]');
  const root = button?.closest('[data-bf-cart-delivery]');
  if (!root || root.dataset.local !== 'true') return;
  if (button.matches('[data-bf-gps-locate]')) {
    locateGps(root);
    return;
  }
  cancelGpsRequest(root);
  setGpsValue(root, '', 'gpsRemoved');
  clearTimeout(saveTimer);
  queueSave(root);
});

initializeRoots();
new MutationObserver((records) => {
  const selector = '[data-bf-cart-delivery], [data-bf-cart-kind]';
  const hasCartChange = records.some((record) => record.type === 'attributes' ||
    [...record.addedNodes, ...record.removedNodes].some((node) => node.nodeType === 1 &&
      (node.matches(selector) || node.querySelector(selector))));
  if (hasCartChange) initializeRoots();
}).observe(document.documentElement, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['data-local', 'data-bf-cart-kind', 'data-bf-has-delivery-preferences'],
});
